import { useDiaryPayments } from "../booking/useDiaryPayments.js";
import { useDiaryBlocks } from "../booking/useDiaryBlocks.js";
import { scheduleBlockOutcome } from "../booking/scheduleBlockOutcome.js";
import { ActionConfirmationHost, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { get, type BookingList, type Me } from "../api.js";
import { isOwner, useAuth } from "../auth.js";
import { ScheduleBlockForm, scheduleBlockFormFor } from "../booking/ScheduleBlockForm.js";
import { scheduleBlocks, type ScheduleBlockApply, type ScheduleBlockInput, type StaffScheduleBlock } from "../booking/scheduleBlocks.js";
import {
  resolveScheduleBlockTarget,
  scheduling,
} from "../booking/scheduling.js";
import type { Period } from "@wmf/shared/workingHours";
import {
  blockSpansOnDay,
  describeCalendarBlock,
  type CalendarBlockSpan,
} from "../calendar/blocks.js";
import { DateField } from "../calendar/DateField.js";
import { DiaryPaymentKey } from "../calendar/DiaryPaymentKey.js";
import { LessonActionDrawer } from "../calendar/LessonActionDrawer.js";
import { Month } from "../calendar/Month.js";
import { MiniMonth } from "../calendar/MiniMonth.js";
import { useDiaryToday } from "../calendar/useDiaryClock.js";
import { TimeGrid, type Column } from "../calendar/TimeGrid.js";
import { diaryPublication, nextLessonAfterPublication } from "../calendar/lessonPublication.js";
import { applyPatches, confirmedBy, type ScheduleChange } from "../calendar/pendingPatches.js";
import {
  applyWhose,
  colourOf,
  defaultDiaryWhose,
  diaryEntries,
  hasColour,
  isEverybody,
  isNoShow,
  isNoShowSession,
  isTicked,
  NO_TUTOR,
  signedInTutorId,
  toggleAll,
  toggleTutor,
  tutorOptions,
  tutorsIn,
  type DiaryEntry,
  type TutorOption,
  type Whose,
} from "../calendar/entries.js";
import {
  addDays,
  dayOf,
  daysShown,
  daysBetween,
  isDayKey,
  isSameMonth,
  minutesOfDay,
  sessionsByColumn,
  shift,
  startOfWeek,
  weekdayIndex,
  weekdayOf,
  weekOf,
  windowFor,
  type DayKey,
  type Session,
  type View,
} from "../calendar/grid.js";
import {
  dayKey,
  dayNumber,
  formatDate,
  formatDay,
  formatMonthOf,
  formatTime,
  formatWeekdayDay,
  formatWeekOf,
} from "../format.js";
import { useRememberedChoice } from "../preferences.js";
import { useNewSinceLoad } from "../sync.js";
import { AddAppointmentDrawer } from "../calendar/AddAppointmentDrawer.js";
import { BookLessonDrawer, type BookLessonHint } from "../calendar/BookLessonDrawer.js";
import { Drawer } from "../ui/Dialog.js";
import { useAsync } from "../useAsync.js";
import { measureAction } from "../telemetry/actionTiming.js";
import { TeachingDay } from "../calendar/TeachingDay.js";

export const DIARY_MUTATION_REFRESH_DELAYS = [5_000] as const;

/**
 * The diary: what is actually happening, in the order it happens.
 *
 * Bookings arrive as a flat list and are grouped into London days here rather
 * than on the server, because the grouping is a presentation decision and the
 * same list feeds other views.
 *
 * Four ways of reading the same lessons, and the reason there is more than one
 * is that a tutor asks four different questions of a diary. The list answers
 * "what is next" over a stretch of any length. The week answers "what does
 * Thursday look like" — to scale, so the gaps are visible, which is what a list
 * cannot show. The month answers "which weeks am I teaching" and is
 * deliberately too small to work in, drilling through to the week instead.
 *
 * The day answers a question about people rather than about time: "who is
 * teaching what today, and who is free at four". So it is the one view whose
 * columns are tutors and not days, and the one drawn midnight to midnight —
 * the empty hours are the answer to half of that question, and cropping to the
 * hours somebody happens to be booked in would hide exactly what is being
 * looked for.
 *
 * **Whose lessons are in here is the server's decision, not this page's.** A
 * tutor is sent their own lessons in full and everybody else's with nothing on
 * them but a time and who is teaching. The filter below only narrows what has
 * already been decided; it is a convenience and never a control on what may be
 * seen.
 */

/** Which working hours a week-view day column shades against. */
export function weekHoursFor(
  rota: BookingList["rota"],
  chosen: Whose,
  tutors: readonly TutorOption[],
  key: DayKey,
): Period[] | undefined {
  const soleTicked = tutors.filter((tutor) => isTicked(chosen, tutor.value));
  const only = soleTicked.length === 1 ? soleTicked[0] : undefined;
  if (only === undefined) return undefined;

  const on = weekdayIndex(key);
  const entry = rota[only.value];
  if (on === null || entry === undefined) return undefined;
  return entry.periods
    .filter((period) => period.weekday === on)
    .map(({ startsMinutes, endsMinutes }) => ({ startsMinutes, endsMinutes }));
}

/**
 * Match the quick launcher's Book lesson gate. A tutor whose account is not
 * linked to a booking-system provider has no diary to book into, so the add
 * action would only reach "Tutor unavailable".
 */
export function canAddDiaryAppointment(me: Me | null): boolean {
  return me?.booking.staff === true && (isOwner(me) || me.linkedToProvider);
}

function UndoScheduleBlockButton({ onUndo }: { onUndo: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const running = useRef(false);

  async function undo() {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    try {
      await onUndo();
    } finally {
      running.current = false;
      setBusy(false);
    }
  }

  return <button type="button" disabled={busy} onClick={() => void undo()}>{busy ? "Restoring…" : "Undo"}</button>;
}

export function Diary() {
  return <ActionConfirmationHost><DiaryContent /></ActionConfirmationHost>;
}

/**
 * The receipt for a plain block edit, which the form itself does not publish.
 * The form already answers a block it cannot confirm with its own outcome, so
 * the unconfirmed branch here is only a guard for a block that arrives marked
 * as needing attention; it mirrors what the Time off page says.
 */
export function plainBlockEditOutcome(applied: ScheduleBlockApply): ActionOutcome {
  const { block, schedule } = applied;
  if (block.status === "requires_attention") {
    return {
      ...scheduleBlockOutcome(block, "Time off updated"),
      variant: "uncertain",
      title: "Time off change needs checking",
      details: [
        { label: "Reason", value: block.reason },
        { value: "The booking system could not confirm that this change was applied, so the time may or may not be blocked. It is listed under Needs review on the Time off page." },
      ],
      action: <Link to="/time-off">Open Time off</Link>,
    };
  }
  if (schedule.conflict_count === 0) return scheduleBlockOutcome(block, "Time off updated");
  const lessons = schedule.conflicting_appointments;
  return {
    ...scheduleBlockOutcome(block, "Time off updated"),
    variant: "attention",
    title: "Time off updated; lessons overlap",
    details: [
      { label: "Reason", value: block.reason },
      { value: `${lessons.length} booked lesson${lessons.length === 1 ? "" : "s"} overlap this period and were not changed:` },
      ...lessons.map((lesson) => ({
        value: `${lesson.customer_names.join(", ") || "Booked lesson"} · ${formatDate(lesson.starts_at)} · ${formatTime(lesson.starts_at)}–${formatTime(lesson.ends_at)}`,
      })),
    ],
    action: <Link to="/time-off">Review in Time off</Link>,
  };
}

function DiaryContent() {
  const publish = usePublishActionOutcome();
  const location = useLocation();
  const navigate = useNavigate();
  const today = useDiaryToday();
  const [publication] = useState(() => diaryPublication(location.state));
  const [nextLesson, setNextLesson] = useState<(DiaryEntry & { anonymous: false }) | null>(null);
  const [positionedAfterPublish, setPositionedAfterPublish] = useState(false);
  const publicationPositioned = useRef(false);
  const nextFrom = publication
    ? dayOf(new Date(Math.max(Date.parse(publication.startsAt), Date.parse(publication.returnedAt))).toISOString())
    : today;
  const [addingAppointment, setAddingAppointment] = useState(false);
  const { me } = useAuth();
  const preferenceScope = me?.user.id ?? "anonymous";
  // View mode is a settled preference and stays in localStorage. The working
  // filters below are deliberately only session-persistent: a reload during a
  // shift keeps your place, but a later browser session starts safely afresh.
  // Both are account-scoped so a second member of staff using the same browser
  // never inherits somebody else's diary workspace.
  const [view, setView] = useRememberedChoice<View>(
    `diary.${preferenceScope}.view`,
    VIEWS,
    "list",
  );

  const [anchor, setAnchorState] = useState<DayKey>(() =>
    publication ? nextFrom : readSessionDay(preferenceScope, "anchor", today),
  );
  const [from, setFromState] = useState(() =>
    readSessionDay(preferenceScope, "from", today),
  );
  const [to, setToState] = useState(() =>
    readSessionDay(preferenceScope, "to", addDays(today, 13)),
  );
  const [whose, setWhoseState] = useState<Whose | null>(() =>
    readSessionWhose(preferenceScope),
  );

  function setAnchor(next: DayKey) {
    setAnchorState(next);
    writeDiarySession(preferenceScope, "anchor", next);
  }
  function setFrom(next: string) {
    setFromState(next);
    writeDiarySession(preferenceScope, "from", next);
  }
  function setTo(next: string) {
    setToState(next);
    writeDiarySession(preferenceScope, "to", next);
  }
  function setWhose(next: Whose) {
    setWhoseState(next);
    writeDiarySession(
      preferenceScope,
      "whose",
      next === "all" ? "all" : JSON.stringify([...next]),
    );
  }

  useEffect(() => {
    if (!publication) return;
    setView("day");
    // Consume the navigation request so reload/back does not repeat the jump.
    navigate("/diary", { replace: true, state: null });
  }, [publication, navigate, setView]);

  const upcoming = useAsync(
    () => publication
      ? get<BookingList>("/admin/bookings", { from: nextFrom, to: addDays(nextFrom, 13) })
      : Promise.resolve(null),
    [publication, nextFrom],
  );
  const [selectedBlock, setSelectedBlock] = useState<CalendarBlockSpan | null>(
    null,
  );
  const [blockEditState, setBlockEditState] = useState<
    | { status: "loading"; providerId: number; blockId: string }
    | { status: "error"; providerId: number; blockId: string; message: string }
    | { status: "ready"; providerId: number; blockId: string; block: StaffScheduleBlock }
    | null
  >(null);
  const blockEditRequest = useRef(0);
  const [blockEditBusy, setBlockEditBusy] = useState(false);
  const [selectedBlockBookingHint, setSelectedBlockBookingHint] = useState<BookLessonHint | null>(null);
  const [blockBookingHint, setBlockBookingHint] = useState<BookLessonHint | null>(null);
  const [blockBusyId, setBlockBusyId] = useState<string | null>(null);
  const [blockProblem, setBlockProblem] = useState<string | null>(null);
  const [selectedLesson, setSelectedLesson] =
    useState<Session<DiaryEntry> | null>(null);
  const [compactList, setCompactList] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 620px)").matches,
  );
  const [listPage, setListPage] = useState(0);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(max-width: 620px)");
    const update = () => setCompactList(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  // The API date contract is UTC; labels and grouping here are London days.
  // Pad the transport window, then clip before deriving counts and actions.
  const asked = view === "list" ? { from: addDays(from, -1), to: addDays(to, 1) } : windowFor(view, anchor);

  const { data, error, loading, reload } = useAsync(
    () =>
      get<BookingList>("/admin/bookings", { from: asked.from, to: asked.to }),
    [asked.from, asked.to],
    { reloadOnMirrorRefresh: true },
  );
  const somethingNew = useNewSinceLoad(loading);
  const [patches, setPatches] = useState<Map<number, ScheduleChange>>(() => new Map());
  const [diaryNotCaughtUp, setDiaryNotCaughtUp] = useState(false);
  const [mutationRefreshStep, setMutationRefreshStep] = useState<
    "start" | "immediate" | "waiting" | "second" | null
  >(null);
  const previousSyncNotice = useRef(somethingNew);
  const pendingSyncRefresh = useRef(false);
  // A change that carries no patch (a new booking) leaves nothing for the
  // immediate read to be checked against, so that read cannot prove the diary
  // has caught up: it still gets its one follow-up.
  const unpatchedChange = useRef(false);
  const refreshAfterMutation = useCallback((change?: ScheduleChange) => {
    // An already-visible banner predates this change. Only a later mirror
    // update can shorten the wait for the one permitted follow-up read.
    previousSyncNotice.current = somethingNew;
    pendingSyncRefresh.current = false;
    if (!change) unpatchedChange.current = true;
    if (change) {
      setPatches((current) => {
        const next = new Map(current);
        next.set(change.appointmentExternalId, change);
        return next;
      });
    }
    setDiaryNotCaughtUp(false);
    setMutationRefreshStep("start");
  }, [somethingNew]);
  useEffect(() => {
    if (somethingNew && !previousSyncNotice.current && mutationRefreshStep !== null) {
      pendingSyncRefresh.current = true;
    }
    previousSyncNotice.current = somethingNew;
    if (mutationRefreshStep === null) return;
    if (mutationRefreshStep === "waiting" && pendingSyncRefresh.current) {
      pendingSyncRefresh.current = false;
      setMutationRefreshStep("second");
      if (!loading) reload();
      return;
    }
    if (loading) return;
    if (mutationRefreshStep === "start") {
      setMutationRefreshStep("immediate");
      reload();
      return;
    }
    if (mutationRefreshStep === "immediate") {
      const serverEntries = data === null ? [] : diaryEntries(data, { includeNoShows: true });
      const confirmed = error === null && !unpatchedChange.current &&
        [...patches.values()].every((patch) => confirmedBy(serverEntries, patch));
      if (confirmed) {
        setPatches(new Map());
        setDiaryNotCaughtUp(false);
        setMutationRefreshStep(null);
        pendingSyncRefresh.current = false;
        unpatchedChange.current = false;
        return;
      }
      if (pendingSyncRefresh.current) {
        pendingSyncRefresh.current = false;
        setMutationRefreshStep("second");
        reload();
        return;
      }
      setMutationRefreshStep("waiting");
      return;
    }
    if (mutationRefreshStep === "second") {
      const serverEntries = data === null ? [] : diaryEntries(data, { includeNoShows: true });
      const confirmed = error === null && [...patches.values()].every((patch) => confirmedBy(serverEntries, patch));
      setPatches(new Map());
      setDiaryNotCaughtUp(!confirmed && patches.size > 0);
      setMutationRefreshStep(null);
      pendingSyncRefresh.current = false;
      unpatchedChange.current = false;
      return;
    }
    const timer = setTimeout(() => {
      pendingSyncRefresh.current = false;
      setMutationRefreshStep("second");
      reload();
    }, DIARY_MUTATION_REFRESH_DELAYS[0]);
    return () => clearTimeout(timer);
  }, [data, error, loading, mutationRefreshStep, patches, reload, somethingNew]);
  useEffect(() => {
    pendingSyncRefresh.current = false;
    unpatchedChange.current = false;
    setMutationRefreshStep(null);
    setPatches(new Map());
    setDiaryNotCaughtUp(false);
  }, [asked.from, asked.to]);
  // Rota is deliberately independent of mirrored lesson rows. A slow or
  // unavailable booking-system working-hours read must not hold the diary
  // itself hostage; the grid renders first and adds shading when this arrives.
  const rotaRead = useAsync(
    () => get<{ rota: BookingList["rota"] }>("/admin/bookings/rota"),
    [],
  );

  /**
   * Schedule blocks are provider-scoped, while diary columns are Hub tutor
   * profiles. Use the same scheduling identity seam as the Time off page rather
   * than guessing a provider from a name or from a lesson.
   */
  const canManageOtherBlocks =
    me !== null &&
    (isOwner(me) || me.capabilities.includes("schedules.manage.all"));
  const ownSchedule = useAsync(() => scheduling.me(), []);
  const managedTutors = useAsync(
    () => (canManageOtherBlocks ? scheduling.tutors() : Promise.resolve([])),
    [canManageOtherBlocks],
  );

  // The blank-slot drawer reuses this already-loaded identity rather than
  // re-hitting /scheduling/me and /scheduling/tutors on every empty-cell click.
  const resolveBlockTarget = useCallback(
    (tutorId: string | undefined) =>
      resolveScheduleBlockTarget(
        tutorId,
        canManageOtherBlocks,
        ownSchedule.data,
        managedTutors.data ?? [],
      ),
    [canManageOtherBlocks, ownSchedule.data, managedTutors.data],
  );
  const blockTargetLoading =
    ownSchedule.loading || (canManageOtherBlocks && managedTutors.loading);
  const blockTargetError =
    ownSchedule.error || (canManageOtherBlocks ? managedTutors.error : null);

  const all = applyPatches(diaryEntries(data, { includeNoShows: true }), patches).filter(entry => view !== "list" ||
    (dayOf(entry.startsAt) >= from && dayOf(entry.startsAt) <= to));
  const rota = rotaRead.data?.rota ?? {};
  const weekday = weekdayIndex(anchor);

  const hoursOn = (tutorId: string): Period[] | undefined => {
    const entry = rota[tutorId];
    if (entry === undefined || weekday === null) return undefined;
    return entry.periods
      .filter((period) => period.weekday === weekday)
      .map(({ startsMinutes, endsMinutes }) => ({
        startsMinutes,
        endsMinutes,
      }));
  };

  /**
   * Start on the signed-in teacher's own appointments, including when that
   * account also has the owner role. "Everyone" is still available as an
   * explicit choice and, once chosen, the session-persisted filter wins here.
   *
   * Prefer the auth bootstrap identity so the first paint is already correct.
   * Older tests/clients can still fall back to the scheduling read; only a
   * non-owner may use the mirror-derived fallback, because an owner receives
   * everybody's named rows and cannot infer their own identity from them.
   */
  const mirroredMine = signedInTutorId(all);
  const mine =
    me?.tutorIdentity?.tutorProfileId ??
    ownSchedule.data?.tutorProfileId ??
    (isOwner(me) ? null : mirroredMine);

  const byDefault = defaultDiaryWhose(mine);
  const chosen = whose ?? byDefault;
  const providerByTutor = new Map<string, number>();
  if (ownSchedule.data?.tutorProfileId && ownSchedule.data.providerId !== null) {
    providerByTutor.set(ownSchedule.data.tutorProfileId, ownSchedule.data.providerId);
  }
  if (canManageOtherBlocks) {
    for (const tutor of managedTutors.data ?? []) {
      providerByTutor.set(tutor.tutorProfileId, tutor.providerId);
    }
  }
  const selectedProviders = [...providerByTutor].filter(([tutorId]) => isTicked(chosen, tutorId));
  const selectedTutorIds = chosen === "all"
    ? [...new Set([...providerByTutor.keys(), ...tutorsIn(all).map((tutor) => tutor.value)])]
    : [...chosen];
  // Everyone is ambiguous until the full selection is known. The ordinary
  // explicit own-tutor filter can still start its week read immediately.
  const weekSelectionSettled = chosen !== "all" ||
    ((!loading || data !== null) && !ownSchedule.loading && (!canManageOtherBlocks || !managedTutors.loading));
  const weekProviderId = view === "week" && weekSelectionSettled && selectedTutorIds.length === 1
    ? providerByTutor.get(selectedTutorIds[0] ?? "") ?? null : null;
  const blockWindow = view === "week" ? weekOf(anchor) : [anchor];
  const activeBlocks = useDiaryBlocks(
    view === "day" ? selectedProviders.map(([, providerId]) => providerId)
      : weekProviderId === null ? [] : [weekProviderId],
    blockWindow[0] ?? anchor,
    blockWindow[blockWindow.length - 1] ?? anchor,
    view === "day" || view === "week",
    preferenceScope,
  );

  const onTheDay = all.filter((entry) => dayOf(entry.startsAt) === anchor);
  const tutors = view === "day" ? tutorsOnTheDay() : tutorsInWindow();

  function tutorsOnTheDay(): TutorOption[] {
    const byId = new Map<string, string | null>();
    let unattributed = false;

    for (const entry of onTheDay) {
      if (entry.tutorId === null) unattributed = true;
      else if (!byId.has(entry.tutorId))
        byId.set(entry.tutorId, entry.tutorName);
    }

    for (const [tutorId, entry] of Object.entries(rota)) {
      if (weekday === null) break;
      if (!entry.periods.some((period) => period.weekday === weekday)) continue;
      if (!byId.has(tutorId)) byId.set(tutorId, entry.name);
    }

    const ownId = me?.tutorIdentity?.tutorProfileId ?? ownSchedule.data?.tutorProfileId;
    if (ownId && !byId.has(ownId)) {
      byId.set(ownId, me?.tutorIdentity?.displayName ?? ownSchedule.data?.displayName ?? "You");
    }
    for (const [tutorId, providerId] of selectedProviders) {
      if (byId.has(tutorId) || !blockSpansOnDay(
        activeBlocks.blocks.filter((block) => block.provider_id === providerId), anchor,
      ).length) continue;
      byId.set(tutorId, managedTutors.data?.find((tutor) => tutor.tutorProfileId === tutorId)?.displayName ?? "You");
    }

    return tutorOptions(byId, unattributed);
  }

  /**
   * A tutor with a completely empty week still needs their own calendar column
   * context so a holiday can be visible. Do not widen this to every rota entry:
   * the week filter remains driven by lessons, with only the signed-in tutor
   * added when the authoritative scheduling identity knows who they are.
   */
  function tutorsInWindow(): TutorOption[] {
    const options = tutorsIn(all);
    const ownId =
      me?.tutorIdentity?.tutorProfileId ?? ownSchedule.data?.tutorProfileId;
    if (!ownId || options.some((option) => option.value === ownId))
      return options;
    return [
      ...options,
      {
        value: ownId,
        label:
          me?.tutorIdentity?.displayName ?? ownSchedule.data?.displayName ?? "You",
      },
    ].sort((a, b) => a.label.localeCompare(b.label));
  }

  useEffect(() => {
    if (!publication || publicationPositioned.current || upcoming.loading || ownSchedule.loading) return;
    const next = upcoming.error ? null : nextLessonAfterPublication(diaryEntries(upcoming.data), publication, mine);
    publicationPositioned.current = true;
    setNextLesson(next);
    setPositionedAfterPublish(true);
    if (next) {
      setAnchor(dayOf(next.startsAt));
      setWhose(new Set([next.tutorId ?? NO_TUTOR]));
    }
  }, [publication, upcoming.loading, upcoming.error, upcoming.data, ownSchedule.loading, mine]);
  const shown = applyWhose(all, chosen);
  const nobodyTicked = !loading && all.length > 0 && shown.length === 0 &&
    !((view === "day" || view === "week") && tutors.some((tutor) => isTicked(chosen, tutor.value)));

  // List/month still receive one row per booking from the Hub. Collapse them
  // into the same appointment-shaped sessions the grids use before opening an
  // action drawer, otherwise a group lesson clicked in the list would pretend
  // to be a one-student appointment.
  const actionSessions =
    sessionsByColumn(shown, ["diary"], () => "diary").get("diary") ?? [];
  const sessionByAppointment = new Map(
    actionSessions.map((session) => [session.appointmentId, session] as const),
  );

  useEffect(() => {
    const appointmentId = selectedLesson?.appointmentId;
    if (appointmentId === undefined) return;

    const latestAll = diaryEntries(data, { includeNoShows: true }).filter((entry) => view !== "list" ||
      (dayOf(entry.startsAt) >= from && dayOf(entry.startsAt) <= to));
    const latestShown = applyWhose(latestAll, whose ?? defaultDiaryWhose(mine));
    const latestSession = (sessionsByColumn(latestShown, ["diary"], () => "diary").get("diary") ?? [])
      .find((session) => session.appointmentId === appointmentId);
    if (!latestSession) return;
    setSelectedLesson((current) =>
      current?.appointmentId === appointmentId && current !== latestSession ? latestSession : current,
    );
  }, [data, from, mine, selectedLesson?.appointmentId, to, view, whose]);

  function openLesson(appointmentId: string) {
    const session = sessionByAppointment.get(appointmentId);
    if (session) setSelectedLesson(session);
  }

  const drawn = view === "list" ? null : new Set(daysShown(view, anchor));
  const visible = drawn
    ? shown.filter((entry) => drawn.has(dayOf(entry.startsAt)))
    : shown;
  const tutorsKey = chosen === "all" ? "all" : [...chosen].sort().join(",");
  const visibleSessions = actionSessions.filter((session) => !drawn || drawn.has(dayOf(session.startsAt)));
  const noShowSessions = visibleSessions.filter(isNoShowSession).length;
  const tally =
    view === "list"
      ? {
          count: visible.filter((entry) => !isNoShow(entry)).length,
          noShows: visible.filter(isNoShow).length,
          noun: "booking",
        }
      : {
          count: visibleSessions.length - noShowSessions,
          noShows: noShowSessions,
          noun: "lesson",
        };

  // A week column is a day, not a tutor. Only when exactly one tutor is selected
  // can a blank week cell unambiguously prefill a tutor. A tutor's normal own
  // diary has exactly that shape; owners looking at everybody can still click
  // a time and choose the tutor on the booking screen.
  const soleTutor = tutors.filter((tutor) => isTicked(chosen, tutor.value));
  const soleTutorId =
    soleTutor.length === 1 && soleTutor[0]?.value !== NO_TUTOR
      ? soleTutor[0]?.value
      : undefined;

  const columns: Column[] =
    view === "day"
      ? tutors
          .filter((tutor) => isTicked(chosen, tutor.value))
          .map((tutor) => ({
            key: tutor.value,
            heading: tutor.label,
            highlight: tutor.value === mine,
            day: anchor,
            tutorId: tutor.value === NO_TUTOR ? undefined : tutor.value,
          }))
      : daysShown("week", anchor).map((day) => ({
          key: day,
          heading: weekdayOf(day),
          subheading: dayNumber(day),
          highlight: day === today,
          day,
          tutorId: soleTutorId,
        }));

  const myTeachingDayPaymentIds =
    view === "day" && mine !== null
      ? all.flatMap((entry) =>
          !entry.anonymous &&
          entry.tutorId === mine &&
          dayOf(entry.startsAt) === anchor &&
          entry.externalId !== null
            ? [entry.externalId]
            : [],
        )
      : [];
  const paymentIds = [...new Set([
    ...visible.flatMap((entry) => !entry.anonymous && entry.externalId !== null ? [entry.externalId] : []),
    ...myTeachingDayPaymentIds,
  ])];
  // Speculate while bookings load so ordinary navigation remains parallel.
  // Once known, a failed/empty grid has no markers to read or gap-fill.
  const paymentGridEnabled = (view === "day" || view === "week") && !error &&
    (loading || (!nobodyTicked && columns.length > 0 && paymentIds.length > 0));
  const { payments, refreshAll, refreshOne } = useDiaryPayments(
    asked, tutorsKey, paymentGridEnabled, paymentIds,
  );

  const columnOf = (entry: DiaryEntry): string =>
    view === "day" ? (entry.tutorId ?? NO_TUTOR) : dayOf(entry.startsAt);

  const hoursFor = (key: string): Period[] | undefined =>
    view === "day" ? hoursOn(key) : weekHoursFor(rota, chosen, tutors, key);

  function blocksFor(key: string): CalendarBlockSpan[] {
    if (view === "day") {
      const providerId = providerByTutor.get(key);
      if (providerId === undefined) return [];
      return blockSpansOnDay(
        activeBlocks.blocks.filter(
          (block) => block.provider_id === providerId,
        ),
        anchor,
      );
    }
    // "Everyone" can also show anonymous colleagues whose providers this
    // principal cannot read. Never paint one tutor's blocks over that mix.
    if (view === "week" && weekProviderId !== null && soleTutor.length <= 1) {
      return blockSpansOnDay(
        activeBlocks.blocks.filter(
          (block) => block.provider_id === weekProviderId,
        ),
        key,
      );
    }
    return [];
  }

  useEffect(() => {
    blockEditRequest.current += 1;
    setBlockEditState(null);
    setSelectedBlock(null);
    setSelectedBlockBookingHint(null);
    setBlockBookingHint(null);
    setSelectedLesson(null);
    setBlockProblem(null);
  }, [anchor, view, whose]);

  function loadSelectedBlockForEdit() {
    if (selectedBlock === null || blockBusyId !== null) return;
    if (blockEditState?.status === "ready") {
      blockEditRequest.current += 1;
      setBlockEditState(null);
      return;
    }

    const { providerId, blockId } = selectedBlock;
    const request = ++blockEditRequest.current;
    setBlockEditState({ status: "loading", providerId, blockId });
    void scheduleBlocks.read(providerId, blockId)
      .then((block) => {
        if (request !== blockEditRequest.current) return;
        if (block.status !== "active") {
          setBlockEditState({
            status: "error",
            providerId,
            blockId,
            message: "This blocked time needs review before it can be edited.",
          });
          return;
        }
        setBlockEditState({ status: "ready", providerId, blockId, block });
      })
      .catch((cause: unknown) => {
        if (request !== blockEditRequest.current) return;
        setBlockEditState({
          status: "error",
          providerId,
          blockId,
          message: cause instanceof Error ? cause.message : "We could not load that block.",
        });
      });
  }

  function closeSelectedBlock() {
    blockEditRequest.current += 1;
    setBlockEditState(null);
    setSelectedBlock(null);
  }

  async function removeSelectedBlock() {
    if (selectedBlock === null || blockBusyId !== null) return;

    setBlockBusyId(selectedBlock.blockId);
    setBlockProblem(null);
    try {
      const removed = await measureAction("block.remove", () =>
        scheduleBlocks.remove(
          selectedBlock.providerId,
          selectedBlock.blockId,
        ),
      );
      publish({
        ...scheduleBlockOutcome(removed, "Blocked time removed", true),
        action: <UndoScheduleBlockButton onUndo={() => restoreRemovedBlock(removed)} />,
      });
      blockEditRequest.current += 1;
      setBlockEditState(null);
      setSelectedBlock(null);
      activeBlocks.reload(removed.provider_id);
    } catch (cause) {
      setBlockProblem(
        cause instanceof Error
          ? cause.message
          : "We could not confirm that the blocked time was removed. Refresh the diary to check before trying again.",
      );
    } finally {
      setBlockBusyId(null);
    }
  }

  async function restoreRemovedBlock(block: Awaited<ReturnType<typeof scheduleBlocks.remove>>) {
    const input: ScheduleBlockInput = {
      block_date: block.block_date,
      end_date: block.end_date,
      start_time: block.start_time,
      end_time: block.end_time,
      reason: block.reason,
    };

    try {
      const preview = await scheduleBlocks.preview(block.provider_id, input);
      if (preview.conflict_count > 0) {
        publish({
          variant: "attention",
          title: "Could not restore automatically",
          details: [{ value: "A lesson is now booked in that time. Open Time off to add the block again." }],
        });
        return;
      }

      const restored = await scheduleBlocks.create(block.provider_id, input);
      publish(scheduleBlockOutcome(restored.block, "Blocked time restored"));
      activeBlocks.reload(block.provider_id);
    } catch (cause: unknown) {
      publish({
        variant: "attention",
        title: "Could not restore blocked time",
        details: [{
          value: cause instanceof Error
            ? cause.message
            : "Refresh the diary and check the schedule before trying again.",
        }],
      });
    }
  }

  const days = new Map<string, DiaryEntry[]>();
  for (const entry of shown) {
    const key = dayKey(entry.startsAt);
    const list = days.get(key);
    if (list) list.push(entry);
    else days.set(key, [entry]);
  }
  const listDays = [...days.entries()];
  const listPageSize = compactList ? 1 : 7;
  const listPageCount = Math.max(1, Math.ceil(listDays.length / listPageSize));
  const safeListPage = Math.min(listPage, listPageCount - 1);
  const visibleListDays = listDays.slice(
    safeListPage * listPageSize,
    (safeListPage + 1) * listPageSize,
  );

  useEffect(() => {
    setListPage(0);
  }, [from, to, whose, compactList]);

  const openDay = (day: DayKey) => {
    setAnchor(day);
    setView("day");
  };

  const openMiniDay = (day: DayKey) => {
    if (view !== "list") {
      openDay(day);
      return;
    }
    const span = daysBetween(from, to);
    setFrom(day);
    setTo(addDays(day, span !== null && span >= 0 ? span : 13));
    setAnchor(day);
  };

  const listRange = view === "list" && isDayKey(from) && isDayKey(to) && from <= to
    ? { from, to } : undefined;

  const absenceFrom = view === "list" ? from : anchor;
  const absenceTo = view === "list" ? to : anchor;
  const absenceRangeValid = isDayKey(absenceFrom) && isDayKey(absenceTo) && absenceFrom <= absenceTo;
  const absenceHref = `/time-off?from=${encodeURIComponent(absenceFrom)}&to=${encodeURIComponent(absenceTo)}`;

  const week = weekOf(anchor);
  const period =
    view === "day"
      ? formatWeekdayDay(anchor)
      : view === "week"
        ? formatWeekOf(week[0] ?? anchor, week[6] ?? anchor)
        : formatMonthOf(anchor);

  const showingToday =
    view === "day"
      ? anchor === today
      : view === "week"
        ? startOfWeek(anchor) === startOfWeek(today)
        : isSameMonth(anchor, today);

  const blockLayerError =
    view === "day" || view === "week"
      ? ownSchedule.error ||
        (canManageOtherBlocks ? managedTutors.error : null) ||
        activeBlocks.error
      : null;

  return (
    <>
      <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
        <h1>Diary</h1>
        {me?.booking.staff && absenceRangeValid && (
          <Link className="action-secondary" to={absenceHref}>Plan time off for selected dates</Link>
        )}
        {canAddDiaryAppointment(me) && (
          <button type="button" onClick={() => setAddingAppointment(true)}>
            Add appointment
          </button>
        )}
      </div>
      {addingAppointment && (
        <AddAppointmentDrawer
          canChooseTutor={isOwner(me)}
          onClose={() => setAddingAppointment(false)}
          onBooked={() => {
            setAddingAppointment(false);
            refreshAfterMutation();
          }}
        />
      )}
      <p className="lede">
        {isOwner(me)
          ? "Lessons in the WMF diary, as they stand right now."
          : "Your lessons, as they stand right now."}
      </p>

      {publication && (
        <div className="notice" role="status">
          <strong>Lesson note published.</strong>{" "}
          {!positionedAfterPublish ? "Finding your next lesson…" : upcoming.error
            ? "The next lesson could not be loaded. You can check the diary below."
            : nextLesson
              ? <>Next lesson: {nextLesson.studentName ?? nextLesson.customerName ?? "Lesson"} · {formatDay(nextLesson.startsAt)} at {formatTime(nextLesson.startsAt)}.</>
              : "No upcoming lessons in the next 14 days."}
        </div>
      )}

      {somethingNew && mutationRefreshStep === null && (
        <div className="notice row">
          <span className="small">
            Bookings have changed since this page loaded.
          </span>
          <button className="quiet small" onClick={reload}>
            Refresh
          </button>
        </div>
      )}
      {diaryNotCaughtUp && (
        <div className="notice small" role="status">
          The diary hasn&apos;t caught up with the last change yet. Refresh to check.
        </div>
      )}

      <div className="panel cal-bar">
        <MiniMonth
          selected={view === "list" && isDayKey(from) ? from : anchor}
          today={today}
          range={listRange}
          onSelectDay={openMiniDay}
        />
        <div className="cal-toolbar">
          <div className="row cal-toolbar-main">
            <div
              className="cal-views"
              role="group"
              aria-label="How to show the diary"
            >
              {VIEWS.map((option) => (
                <button
                  aria-pressed={view === option}
                  className={`cal-view${view === option ? " is-on" : ""}`}
                  key={option}
                  onClick={() => setView(option)}
                  type="button"
                >
                  {VIEW_LABEL[option]}
                </button>
              ))}
            </div>

            {view === "list" ? (
              <>
                <DateField label="From" onChange={setFrom} value={from} />
                <DateField label="To" onChange={setTo} value={to} />
              </>
            ) : (
              <>
                <div className="row cal-navigation" role="group" aria-label="Diary date navigation">
                  <button
                    aria-label={`Previous ${view}`}
                    className="quiet"
                    onClick={() => setAnchor(shift(view, anchor, -1))}
                    type="button"
                  >
                    ←
                  </button>
                  <strong className="cal-period">{period}</strong>
                  <button
                    aria-label={`Next ${view}`}
                    className="quiet"
                    onClick={() => setAnchor(shift(view, anchor, 1))}
                    type="button"
                  >
                    →
                  </button>
                </div>
                {!showingToday && (
                  <button
                    className="quiet small"
                    onClick={() => setAnchor(today)}
                    type="button"
                  >
                    Today
                  </button>
                )}
              </>
            )}

            <span className="muted small" style={{ marginLeft: "auto" }}>
              {loading
                ? "Loading…"
                : error ? "Lessons unavailable for this range"
                : `${tally.count} ${tally.noun}${tally.count === 1 ? "" : "s"}${tally.noShows > 0 ? ` · ${tally.noShows} no-show${tally.noShows === 1 ? "" : "s"}` : ""}`}
            </span>

            <DiaryPaymentKey />
          </div>

          {tutors.length > 1 && (
            <div
              className="cal-whose"
              role="group"
              aria-label="Whose lessons to show"
            >
              <span className="small muted">Showing</span>
              <label className="cal-tick">
                <input
                  checked={isEverybody(chosen, tutors)}
                  onChange={() => setWhose(toggleAll(chosen, tutors))}
                  type="checkbox"
                />
                Everyone
              </label>
              {tutors.map((tutor) => (
                <label className="cal-tick" key={tutor.value}>
                  <input
                    checked={isTicked(chosen, tutor.value)}
                    onChange={() =>
                      setWhose(toggleTutor(chosen, tutors, tutor.value))
                    }
                    type="checkbox"
                  />
                  {tutor.label}
                  {tutor.value === mine && <span className="muted"> (you)</span>}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {rotaRead.error && (
        <div className="notice small">
          Working-hours shading is unavailable just now. Lesson times below are
          still current.
        </div>
      )}
      {blockLayerError && (
        <div className="error">
          Blocked time could not be loaded, so this grid may not show every
          unavailable period.
        </div>
      )}

      {nobodyTicked && (
        <p className="empty">
          Nobody is ticked, so there is nothing to show. Tick a name above.
        </p>
      )}

      {view === "day" &&
        mine !== null &&
        me?.teachingDayBulkAttendanceEnabled === true &&
        !loading &&
        !error &&
        data !== null && (
          <TeachingDay
            day={anchor}
            entries={all}
            onMarked={reload}
            payments={payments}
            tutorId={mine}
          />
        )}

      {(view === "day" || view === "week") &&
        !nobodyTicked &&
        columns.length > 0 && (
          <TimeGrid
            payments={payments}
            refreshAll={refreshAll}
            refreshOne={refreshOne}
            blockBusyId={blockBusyId}
            blocksFor={blocksFor}
            columnOf={columnOf}
            columns={columns}
            entries={visible}
            focusAppointmentId={!loading ? nextLesson?.appointmentId : null}
            focusMinute={publication && positionedAfterPublish && !nextLesson && anchor === dayOf(publication.returnedAt)
              ? minutesOfDay(publication.returnedAt) : null}
            hoursFor={hoursFor}
            onBlockClick={(block, hint) => {
              blockEditRequest.current += 1;
              setBlockEditState(null);
              setSelectedBlock(block);
              setSelectedBlockBookingHint(hint);
              setBlockProblem(null);
            }}
            onBlockCreated={activeBlocks.reload}
            resolveBlockTarget={resolveBlockTarget}
            blockTargetLoading={blockTargetLoading}
            blockTargetError={blockTargetError}
            wholeDay={view === "day"}
            onChanged={reload}
            onScheduleChanged={refreshAfterMutation}
          />
        )}

      {selectedBlock && (view === "day" || view === "week") && (
        <Drawer
          eyebrow="Blocked time"
          title={selectedBlock.reason}
          description={describeCalendarBlock(selectedBlock)}
          closeLabel="Close blocked time"
          className="cal-lesson-drawer cal-block-drawer"
          busy={blockBusyId !== null || blockEditBusy}
          onClose={closeSelectedBlock}
        >
          {blockProblem && <div className="error" role="alert">{blockProblem}</div>}
          <div className="cal-block-actions">
            {selectedBlockBookingHint && (
              <button
                className="button action-primary"
                type="button"
                disabled={blockBusyId !== null || blockEditBusy}
                onClick={() => {
                  blockEditRequest.current += 1;
                  setBlockEditState(null);
                  setBlockBookingHint(selectedBlockBookingHint);
                  setSelectedBlock(null);
                }}
              >
                Book lesson over block
              </button>
            )}
            <button
              className="button action-secondary"
              type="button"
              disabled={blockBusyId !== null || blockEditBusy || blockEditState?.status === "loading"}
              onClick={loadSelectedBlockForEdit}
            >
              {blockEditState?.status === "ready"
                ? "Close editor"
                : blockEditState?.status === "error"
                  ? "Try again"
                  : "Edit block"}
            </button>
            <button
              className="action-danger"
              type="button"
              disabled={blockBusyId !== null || blockEditBusy}
              onClick={() => void removeSelectedBlock()}
            >
              Remove block
            </button>
          </div>
          {blockEditState?.status === "loading" && <p className="small muted">Loading…</p>}
          {blockEditState?.status === "error" && (
            <div>
              <div className="error" role="alert">{blockEditState.message}</div>
              <Link
                className="button action-secondary"
                to={`/time-off?provider=${blockEditState.providerId}&edit=${encodeURIComponent(blockEditState.blockId)}`}
              >
                Open in Time off
              </Link>
            </div>
          )}
          {blockEditState?.status === "ready" && (
            <ScheduleBlockForm
              key={`${blockEditState.providerId}:${blockEditState.block.id}`}
              providerId={blockEditState.providerId}
              initial={scheduleBlockFormFor(blockEditState.block)}
              blockId={blockEditState.block.id}
              onCancel={() => setBlockEditState(null)}
              onBusyChange={setBlockEditBusy}
              onSaved={(applied, outcome) => {
                publish(outcome ?? plainBlockEditOutcome(applied));
                blockEditRequest.current += 1;
                setBlockEditState(null);
                activeBlocks.reload(applied.block.provider_id);
              }}
            />
          )}
        </Drawer>
      )}

      {blockBookingHint && (
        <BookLessonDrawer
          key={`${blockBookingHint.tutorId ?? "choose"}:${blockBookingHint.startsAt}`}
          hint={blockBookingHint}
          onClose={() => setBlockBookingHint(null)}
          onBooked={() => {
            setBlockBookingHint(null);
            refreshAfterMutation();
          }}
        />
      )}

      {/* No columns is only reachable on the day, and only once the response
          is in with nobody in it — the week always has its seven. */}
      {view === "day" && !nobodyTicked && !error && !loading && columns.length === 0 && (
        <p className="empty">Nothing booked on this day.</p>
      )}
      {view === "month" && (
        <Month
          anchor={anchor}
          entries={shown}
          onOpenDay={openDay}
          onOpenSession={setSelectedLesson}
          today={today}
        />
      )}

      {view === "list" && (
        <>
          {!error && !loading && days.size === 0 && (
            <p className="empty">Nothing booked in this period.</p>
          )}

          {listDays.length > 0 && (
            <nav
              className="diary-list-pagination"
              aria-label="Diary list dates"
            >
              <button
                className="action-tertiary"
                disabled={safeListPage === 0}
                onClick={() => setListPage(Math.max(0, safeListPage - 1))}
                type="button"
              >
                Previous {compactList ? "day" : "dates"}
              </button>
              <strong>
                {compactList && visibleListDays[0]
                  ? formatDay(
                      visibleListDays[0][1][0]?.startsAt ??
                        visibleListDays[0][0],
                    )
                  : `Dates ${safeListPage * listPageSize + 1}–${Math.min((safeListPage + 1) * listPageSize, listDays.length)} of ${listDays.length}`}
              </strong>
              <button
                className="action-tertiary"
                disabled={safeListPage >= listPageCount - 1}
                onClick={() =>
                  setListPage(Math.min(listPageCount - 1, safeListPage + 1))
                }
                type="button"
              >
                Next {compactList ? "day" : "dates"}
              </button>
            </nav>
          )}

          {visibleListDays.map(([key, entries]) => (
            <section key={key}>
              <h2 className="day-heading">
                {formatDay(entries[0]?.startsAt ?? key)}
              </h2>
              {entries.map((entry) =>
                entry.anonymous ? (
                  <div
                    className={`slot is-other${hasColour(entry) ? " has-colour" : ""}`}
                    key={entry.appointmentId}
                    style={colourOf(entry)}
                  >
                    <time>{formatTime(entry.startsAt)}</time>
                    <div style={{ flex: 1 }}>
                      <div className="muted">
                        {entry.tutorName ?? "No tutor recorded"}
                      </div>
                      <div className="small muted">
                        {entry.serviceName ?? "Lesson"}
                        {entry.isSchools && " · schools"}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`slot cal-list-appointment${isNoShow(entry) ? " is-no-show" : ""}${hasColour(entry) ? " has-colour" : ""}`}
                    key={entry.bookingId}
                    // Clicking the row opens the appointment, but only as a
                    // mouse convenience: the row is deliberately not a button.
                    // ARIA makes a button's children presentational, and this
                    // row carries three links — the student, the tutor and the
                    // write-up — so a screen reader announced all three as
                    // plain text and could follow none of them. The control at
                    // the end of the row is the real one.
                    onClick={(event) => {
                      // Focus it first: the drawer hands focus back to
                      // whatever was active when it opened.
                      event.currentTarget
                        .querySelector<HTMLElement>(".cal-list-open")
                        ?.focus({ preventScroll: true });
                      openLesson(entry.appointmentId);
                    }}
                    style={colourOf(entry)}
                  >
                    <time>{formatTime(entry.startsAt)}</time>
                    <div style={{ flex: 1 }}>
                      <div>
                        {entry.isSchools ? (
                          <span>
                            {entry.studentName ?? entry.customerName ?? "Unknown"}{" "}
                            <span className="tag">School</span>
                          </span>
                        ) : entry.studentId ? (
                          <Link
                            onClick={(event) => event.stopPropagation()}
                            to={`/students/${entry.studentId}`}
                          >
                            {entry.studentName}
                          </Link>
                        ) : (
                          <span className="muted">
                            {entry.customerName ?? "Unknown"}{" "}
                            <span className="tag warn">not matched</span>
                          </span>
                        )}
                        {isNoShow(entry) && <> <span className="cal-no-show-pill">No-show</span></>}
                      </div>
                      <div className="small muted">
                        {entry.serviceName ?? "Lesson"}
                        {entry.attendanceStatus &&
                          ` · ${entry.attendanceStatus}`}
                        {" · "}
                        {entry.tutorId !== null && entry.tutorName !== null ? (
                          <Link
                            onClick={(event) => event.stopPropagation()}
                            to={`/tutors/${entry.tutorId}`}
                          >
                            {entry.tutorName}
                          </Link>
                        ) : (
                          <span>no tutor recorded</span>
                        )}
                      </div>
                    </div>
                    {!entry.isSchools && !isNoShow(entry) && (
                      <Link
                        className="notes-link small"
                        onClick={(event) => event.stopPropagation()}
                        to={`/bookings/${entry.bookingId}/lesson`}
                      >
                        {entry.lessonId === null
                          ? "Write up"
                          : entry.lessonPublishedAt === null
                            ? "Draft"
                            : "Notes ✓"}
                      </Link>
                    )}
                    <button
                      type="button"
                      className="cal-list-open"
                      aria-haspopup="dialog"
                      aria-label={`Open appointment actions for ${entry.studentName ?? entry.customerName ?? "this lesson"}${isNoShow(entry) ? " · no-show" : ""}`}
                      title={`Open appointment actions${isNoShow(entry) ? " · no-show" : ""}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        event.currentTarget.focus({ preventScroll: true });
                        openLesson(entry.appointmentId);
                      }}
                    >
                      <span aria-hidden="true">›</span>
                    </button>
                  </div>
                ),
              )}
            </section>
          ))}
        </>
      )}

      {selectedLesson && (
        <LessonActionDrawer
          key={selectedLesson.appointmentId}
          session={selectedLesson}
          onClose={() => setSelectedLesson(null)}
          onRefresh={() => reload()}
          onChanged={() => {
            setSelectedLesson(null);
            reload();
          }}
          onScheduleChanged={(change) => {
            setSelectedLesson(null);
            refreshAfterMutation(change);
          }}
        />
      )}
    </>
  );
}

/**
 * The four views, in the order they are offered, and the one the browser is
 * allowed to remember somebody choosing.
 *
 * View mode is the one long-lived preference: localStorage keeps it across
 * browser sessions, because which view suits how somebody works is a settled
 * preference. Calendar position and tutor/date filters are not — they are
 * operational state, so the helpers below persist them only in sessionStorage.
 */
const VIEWS = ["list", "day", "week", "month"] as const;

const VIEW_LABEL: Record<View, string> = {
  list: "List",
  day: "Day",
  week: "Week",
  month: "Month",
};

const DIARY_SESSION_PREFIX = "wmf.diary.session.";

function diarySessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function diarySessionKey(scope: string, key: string): string {
  return `${DIARY_SESSION_PREFIX}${scope}.${key}`;
}

function writeDiarySession(scope: string, key: string, value: string): void {
  try {
    diarySessionStorage()?.setItem(diarySessionKey(scope, key), value);
  } catch {
    // A browser refusing storage should never make the diary unusable.
  }
}

function readDiarySession(scope: string, key: string): string | null {
  try {
    return diarySessionStorage()?.getItem(diarySessionKey(scope, key)) ?? null;
  } catch {
    return null;
  }
}

function readSessionDay(scope: string, key: string, fallback: DayKey): DayKey {
  const stored = readDiarySession(scope, key);
  return stored !== null && isDayKey(stored) ? stored : fallback;
}

function readSessionWhose(scope: string): Whose | null {
  const stored = readDiarySession(scope, "whose");
  if (stored === null) return null;
  if (stored === "all") return "all";
  try {
    const parsed: unknown = JSON.parse(stored);
    if (
      !Array.isArray(parsed) ||
      !parsed.every((value) => typeof value === "string")
    )
      return null;
    return new Set(parsed);
  } catch {
    return null;
  }
}
